import { Component, OnDestroy } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PageEvent } from '@angular/material/paginator';
import {
  AbandonTrainingRunRequest,
  GetHomeDetailRequest,
  GetTrainingOverviewRequest,
  GetTrainingRunRequest,
  IfcFile,
  IfcFileArtifact,
  ListHomesRequest,
  ListPagedHomeIfcFilesRequest,
  ListTrainingRulesVersionsRequest,
  ListTrainingRunsRequest,
  PromoteTrainingRunRequest,
  RerunTrainingRunRequest,
  SituViewWebApiClient,
  StartTrainingRunRequest,
  TrainingFileStatus,
  TrainingFlag,
  TrainingFlagSeverity,
  TrainingOverview,
  TrainingRulesVersion,
  TrainingRun,
  TrainingRunStatus,
  TrainingSource,
  TrainingValidation
} from 'situview-webapi-client';
import { AuthService, ConfirmDialogService, StateService } from 'src/app/services';
import { ConfirmDialogData } from 'src/app/components/confirm-dialog/ConfirmDialogData';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';

/** Runs take twenty to eighty five minutes; nothing pushes, so refresh on a slow clock while one is active. */
const ACTIVE_POLL_MS = 60_000;

/** Builder-facing sentence for each run status. Rule ids and field names never appear here. */
export function describeStatus(run: TrainingRun | undefined): string {
  if (!run) {
    return '';
  }
  switch (run.status) {
    case TrainingRunStatus.Requested: return 'Requested, waiting to be picked up';
    case TrainingRunStatus.Queued:
      return run.positionInQueue && run.positionInQueue > 1
        ? `Waiting in the queue, ${run.positionInQueue - 1} ahead`
        : 'Waiting in the queue, next to run';
    case TrainingRunStatus.Running: return 'Training now';
    case TrainingRunStatus.Passed: return run.promotedToRulesVersion ? 'Passed and in use' : 'Passed';
    case TrainingRunStatus.Failed: return 'Did not pass';
    case TrainingRunStatus.Refused: return 'Refused: the files could not be trained from';
    case TrainingRunStatus.Error: return 'Something went wrong on our side';
    case TrainingRunStatus.Cancelled: return 'Cancelled';
    case TrainingRunStatus.NotRequested: return 'Saved but never started';
    case TrainingRunStatus.Abandoned: return 'Abandoned';
    default: return 'Unknown';
  }
}

export function isInProgress(run: TrainingRun | undefined): boolean {
  return !!run && (
    run.status === TrainingRunStatus.Requested ||
    run.status === TrainingRunStatus.Queued ||
    run.status === TrainingRunStatus.Running);
}

/** Passed, Failed and Refused are the run's own verdict; everything else is the platform's or a person's doing. */
export function hasVerdict(run: TrainingRun | undefined): boolean {
  return !!run && (
    run.status === TrainingRunStatus.Passed ||
    run.status === TrainingRunStatus.Failed ||
    run.status === TrainingRunStatus.Refused);
}

/** Runs that never got a verdict and are not being worked on: rerun sends them through again. */
export function needsRerun(run: TrainingRun | undefined): boolean {
  return !!run && (
    run.status === TrainingRunStatus.Error ||
    run.status === TrainingRunStatus.NotRequested ||
    run.status === TrainingRunStatus.Abandoned);
}

/** CSS tone for a status chip. */
export function statusTone(run: TrainingRun | undefined): 'good' | 'bad' | 'busy' | 'muted' {
  if (!run) {
    return 'muted';
  }
  if (isInProgress(run)) {
    return 'busy';
  }
  switch (run.status) {
    case TrainingRunStatus.Passed: return 'good';
    case TrainingRunStatus.Failed:
    case TrainingRunStatus.Refused:
    case TrainingRunStatus.Error: return 'bad';
    default: return 'muted';
  }
}

export function describeElapsed(seconds: number | undefined): string {
  if (seconds === undefined || seconds === null) {
    return '';
  }
  const mins = Math.round(seconds / 60);
  if (mins < 1) {
    return 'under a minute';
  }
  if (mins < 60) {
    return `${mins} min`;
  }
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

@Component({
  selector: 'app-training',
  templateUrl: './training.component.html',
  styleUrls: ['./training.component.less']
})
export class TrainingComponent extends SituBaseComponent implements OnDestroy {
  TrainingRunStatus = TrainingRunStatus;
  TrainingFileStatus = TrainingFileStatus;
  TrainingFlagSeverity = TrainingFlagSeverity;
  TrainingValidation = TrainingValidation;

  // Sentence helpers exposed to the template.
  describeStatus = describeStatus;
  isInProgress = isInProgress;
  needsRerun = needsRerun;
  statusTone = statusTone;
  describeElapsed = describeElapsed;

  // ---- overview and runs ------------------------------------------------------------------
  loading = false;
  loadError: string | undefined;
  overview: TrainingOverview | undefined;
  runs: TrainingRun[] = [];
  rulesVersions: TrainingRulesVersion[] = [];
  showRulesHistory = false;

  // ---- detail -----------------------------------------------------------------------------
  selectedRun: TrainingRun | undefined;
  detailLoading = false;
  detailError: string | undefined;
  downloading: string | undefined;

  // ---- start a run ------------------------------------------------------------------------
  /** idle: the two entry buttons. new: a fresh run. fix: a run seeded from an earlier one's files. */
  mode: 'idle' | 'new' | 'fix' = 'idle';
  /** In fix mode, the run whose files were carried over. */
  basedOn: TrainingRun | undefined;
  feedbackText = '';
  /**
   * Files chosen for the next run, keyed by home and name. Training always takes the latest version
   * of a file (version 0 on the request); if the latest upload is wrong that is fixed at the file
   * level, never by training against an older one. Seeded from a run in fix mode.
   */
  selected = new Map<string, TrainingSource>();
  starting = false;
  acting = false;

  // Home-first picker: one home, then its masters and options.
  homes: string[] = [];
  homesLoading = false;
  homeQuery = '';
  selectedHome: string | undefined;
  homeFiles: IfcFileArtifact[] = [];
  homeFilesLoading = false;

  // Advanced picker: every upload for the company, paged, any mix of homes.
  showAllUploads = false;
  ifcFiles: IfcFile[] = [];
  ifcLoading = false;
  ifcPageNumber = 1;
  ifcPageSize = 10;
  ifcTotal = 0;
  ifcHomeFilter = '';

  private pollHandle: any;
  private ifcFilterHandle: any;

  constructor(
    authService: AuthService,
    stateService: StateService,
    private snackBar: MatSnackBar,
    private dialogService: ConfirmDialogService,
    private situViewApiClient: SituViewWebApiClient
  ) {
    super(authService, stateService);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.stopPolling();
    clearTimeout(this.ifcFilterHandle);
  }

  protected onSelectedCompanyChange(): void {
    this.selectedRun = undefined;
    this.cancelStart();
    this.homes = [];
    this.ifcFiles = [];
    this.loadData();
    this.loadHomes();
  }

  // ---- derived ----------------------------------------------------------------------------

  get activeRun(): TrainingRun | undefined {
    return this.overview?.activeRun;
  }

  get canStart(): boolean {
    return !this.starting && !this.activeRun && this.selected.size > 0 && !!this.selectedCompany?.id;
  }

  /** Shown in place of the start button while a run is in progress; mirrors what the server would refuse with. */
  get busyMessage(): string {
    const run = this.activeRun;
    if (!run) {
      return '';
    }
    return `Run ${run.runNumber} is ${describeStatus(run).toLowerCase()}. One run at a time; start another when it finishes.`;
  }

  get feedbackPrompt(): string {
    return this.mode === 'fix'
      ? 'Let us know of any issues with the latest render, or tell us something that would be helpful in analysing your files.'
      : 'Tell us anything specific about your plans that would be helpful in analysing them.';
  }

  /** Completed runs a fix can be based on, newest first: anything that was actually built. */
  get fixableRuns(): TrainingRun[] {
    return this.runs.filter(r => !isInProgress(r) && (r.sources?.length ?? 0) > 0);
  }

  describeRunChoice(run: TrainingRun): string {
    const when = run.dateCompleted || run.dateRequested;
    const date = when ? new Date(when).toLocaleDateString() : '';
    return `Run ${run.runNumber}: ${describeStatus(run).toLowerCase()}, ${this.describeSources(run)}, ${date}`;
  }

  get currentRulesSentence(): string {
    const rules = this.overview?.currentRules;
    if (!rules) {
      return 'No trained rules yet. Homes convert with Situ\'s starting rules until a run passes.';
    }
    const from = rules.promotedFromRunNumber ? ` from run ${rules.promotedFromRunNumber}` : '';
    return `Rules version ${rules.version} is in use, promoted${from}.`;
  }

  describeSources(run: TrainingRun): string {
    const sources = run.sources ?? [];
    if (!sources.length) {
      return '';
    }
    if (sources.length === 1) {
      return this.describeSource(sources[0]);
    }
    return `${sources.length} files`;
  }

  describeSource(s: TrainingSource): string {
    const version = s.version ? ` v${s.version}` : '';
    return `${s.home} / ${s.name}${version}`;
  }

  describeCounts(run: TrainingRun): string {
    const parts: string[] = [];
    if (run.filesTrained) { parts.push(`${run.filesTrained} trained`); }
    if (run.filesSkipped) { parts.push(`${run.filesSkipped} skipped`); }
    if (run.filesFailed) { parts.push(`${run.filesFailed} failed`); }
    return parts.join(', ');
  }

  describeFileStatus(status: TrainingFileStatus | undefined): string {
    switch (status) {
      case TrainingFileStatus.Trained: return 'Trained';
      case TrainingFileStatus.Skipped: return 'Skipped';
      case TrainingFileStatus.Failed: return 'Failed';
      default: return '';
    }
  }

  describeValidation(v: TrainingValidation | undefined): string {
    switch (v) {
      case TrainingValidation.Passed: return 'checks passed';
      case TrainingValidation.SoftFailures: return 'some checks did not pass';
      case TrainingValidation.HardFailure: return 'checks failed';
      default: return '';
    }
  }

  hasErrorFlag(run: TrainingRun): boolean {
    return (run.flags ?? []).some(f => f.severity === TrainingFlagSeverity.Error);
  }

  /** Passed but not promoted: an error flag held it back, or promotion did not happen for another reason. */
  canPromote(run: TrainingRun): boolean {
    return this.isSituUser && run.status === TrainingRunStatus.Passed && !run.promotedToRulesVersion;
  }

  /** Anyone with start rights, on any run that is not in progress; also how a lost run is sent through again. */
  canRerun(run: TrainingRun): boolean {
    return !this.activeRun && !isInProgress(run);
  }

  /** Staff-only: give up on a run that never reached a verdict so the next one can start. */
  canAbandon(run: TrainingRun): boolean {
    return this.isSituUser && !hasVerdict(run) &&
      run.status !== TrainingRunStatus.Abandoned &&
      run.status !== TrainingRunStatus.Cancelled;
  }

  isSelectedRun(run: TrainingRun): boolean {
    return this.selectedRun?.runNumber === run.runNumber;
  }

  // ---- loading ----------------------------------------------------------------------------

  loadData(quiet: boolean = false): void {
    const companyId = this.selectedCompany?.id;
    if (!companyId) {
      return;
    }

    if (!quiet) {
      this.loading = true;
      this.loadError = undefined;
    }

    Promise.all([
      this.situViewApiClient.getTrainingOverview(new GetTrainingOverviewRequest({ companyId })),
      this.situViewApiClient.listTrainingRuns(new ListTrainingRunsRequest({ companyId })),
      this.situViewApiClient.listTrainingRulesVersions(new ListTrainingRulesVersionsRequest({ companyId }))
    ])
      .then(([overview, runs, versions]) => {
        if (this.selectedCompany?.id !== companyId) {
          return;
        }
        if (!overview.success) {
          this.loadError = overview.errorMessages?.[0] ?? 'Could not load training';
        } else {
          this.overview = overview.overview;
          this.runs = runs.success ? (runs.runs ?? []) : [];
          this.rulesVersions = versions.success ? (versions.versions ?? []) : [];
          this.refreshSelectedFromList();
          this.updatePolling();
        }
        this.loading = false;
      })
      .catch(() => {
        this.loading = false;
        this.loadError = 'Could not load training';
      });
  }

  /** If the run being viewed changed state in the list, reload its detail so the panel is not stale. */
  private refreshSelectedFromList(): void {
    const current = this.selectedRun;
    if (!current) {
      return;
    }
    const summary = this.runs.find(r => r.runNumber === current.runNumber);
    if (summary && (summary.status !== current.status || summary.attempt !== current.attempt)) {
      this.openRun(summary, true);
    }
  }

  private updatePolling(): void {
    if (this.activeRun) {
      if (!this.pollHandle) {
        this.pollHandle = setInterval(() => this.loadData(true), ACTIVE_POLL_MS);
      }
    } else {
      this.stopPolling();
    }
  }

  private stopPolling(): void {
    if (this.pollHandle) {
      clearInterval(this.pollHandle);
      this.pollHandle = undefined;
    }
  }

  // ---- detail -----------------------------------------------------------------------------

  openRun(run: TrainingRun, quiet: boolean = false): void {
    const companyId = this.selectedCompany?.id;
    if (!companyId || run.runNumber === undefined) {
      return;
    }
    if (!quiet && this.isSelectedRun(run)) {
      this.selectedRun = undefined;
      return;
    }

    if (!quiet) {
      this.selectedRun = run;
      this.detailLoading = true;
      this.detailError = undefined;
    }

    const request = new GetTrainingRunRequest({ companyId, runNumber: run.runNumber });
    this.situViewApiClient.getTrainingRun(request)
      .then(response => {
        if (this.selectedRun?.runNumber !== run.runNumber) {
          return;
        }
        if (response.success && response.run) {
          this.selectedRun = response.run;
        } else {
          this.detailError = response.errorMessages?.[0] ?? 'Could not load this run';
        }
        this.detailLoading = false;
      })
      .catch(() => {
        this.detailLoading = false;
        this.detailError = 'Could not load this run';
      });
  }

  closeRun(): void {
    this.selectedRun = undefined;
  }

  /** Staff-only: the flags a builder sees carry the message; code and evidence stay on the Situ side. */
  flagsFor(run: TrainingRun): TrainingFlag[] {
    return run.flags ?? [];
  }

  downloadEvidence(run: TrainingRun, path: string): void {
    const companyId = this.selectedCompany?.id;
    if (!companyId || run.runNumber === undefined || this.downloading) {
      return;
    }
    this.downloading = path;
    this.situViewApiClient.getTrainingRunFile(companyId, run.runNumber, path, run.attempt)
      .then(file => {
        const name = file.fileName || path.split('/').pop() || 'file';
        const url = URL.createObjectURL(file.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = name;
        a.style.visibility = 'hidden';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        this.downloading = undefined;
      })
      .catch(() => {
        this.downloading = undefined;
        this.showMessage(`Could not download ${path}`);
      });
  }

  // ---- home-first picker ------------------------------------------------------------------

  loadHomes(): void {
    const company = this.selectedCompany;
    if (!company?.code) {
      return;
    }
    this.homesLoading = true;
    this.situViewApiClient.listHomes(new ListHomesRequest({ builder: company.code, includeBuilders: false }))
      .then(response => {
        if (this.selectedCompany?.code !== company.code) {
          return;
        }
        this.homes = (response.homes ?? []).slice().sort((a, b) => a.localeCompare(b));
        this.homesLoading = false;
      })
      .catch(() => {
        this.homes = [];
        this.homesLoading = false;
      });
  }

  get filteredHomes(): string[] {
    const q = (this.homeQuery ?? '').trim().toLowerCase();
    const list = q ? this.homes.filter(h => h.toLowerCase().includes(q)) : this.homes;
    return list.slice(0, 50);
  }

  /** Typing clears the chosen home until an option is picked, so stale files never stay on screen. */
  onHomeQueryChange(): void {
    if (this.selectedHome && this.homeQuery !== this.selectedHome) {
      this.selectedHome = undefined;
      this.homeFiles = [];
    }
  }

  chooseHome(home: string): void {
    if (!home) {
      return;
    }
    this.selectedHome = home;
    this.homeQuery = home;
    this.loadHomeFiles(home);
  }

  private loadHomeFiles(home: string): void {
    const company = this.selectedCompany;
    if (!company?.code) {
      return;
    }
    this.homeFilesLoading = true;
    this.homeFiles = [];
    this.situViewApiClient.getHomeDetail(new GetHomeDetailRequest({ builder: company.code, home }))
      .then(response => {
        if (this.selectedHome !== home) {
          return;
        }
        // Only files with a live version can be trained from.
        this.homeFiles = (response.ifcFiles ?? [])
          .filter(f => !!f.name && (f.versions ?? []).some(v => v.active !== false))
          .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
        this.homeFilesLoading = false;
      })
      .catch(() => {
        this.homeFiles = [];
        this.homeFilesLoading = false;
      });
  }

  get masters(): IfcFileArtifact[] {
    return this.homeFiles.filter(f => f.isMaster);
  }

  get options(): IfcFileArtifact[] {
    return this.homeFiles.filter(f => !f.isMaster);
  }

  latestUpload(file: IfcFileArtifact): Date | undefined {
    let latest: Date | undefined;
    for (const v of file.versions ?? []) {
      if (v.dateAdded && (!latest || v.dateAdded > latest)) {
        latest = v.dateAdded;
      }
    }
    return latest;
  }

  private homeFileSource(file: IfcFileArtifact): TrainingSource {
    return new TrainingSource({ home: this.selectedHome, name: file.name, isMaster: !!file.isMaster, version: 0 });
  }

  isHomeFileSelected(file: IfcFileArtifact): boolean {
    return this.selected.has(this.sourceKey({ home: this.selectedHome, name: file.name }));
  }

  toggleHomeFile(file: IfcFileArtifact, checked: boolean): void {
    this.toggleSource(this.homeFileSource(file), checked);
  }

  allSelected(files: IfcFileArtifact[]): boolean {
    return files.length > 0 && files.every(f => this.isHomeFileSelected(f));
  }

  someSelected(files: IfcFileArtifact[]): boolean {
    return files.some(f => this.isHomeFileSelected(f)) && !this.allSelected(files);
  }

  toggleAll(files: IfcFileArtifact[], checked: boolean): void {
    for (const f of files) {
      this.toggleHomeFile(f, checked);
    }
  }

  // ---- advanced picker: every upload -----------------------------------------------------

  toggleAllUploads(): void {
    this.showAllUploads = !this.showAllUploads;
    if (this.showAllUploads && !this.ifcFiles.length) {
      this.loadIfcFiles();
    }
  }

  loadIfcFiles(): void {
    const company = this.selectedCompany;
    if (!company?.code) {
      return;
    }
    this.ifcLoading = true;

    const request = new ListPagedHomeIfcFilesRequest({
      builder: company.code,
      home: this.ifcHomeFilter?.trim() || undefined,
      pageNumber: this.ifcPageNumber,
      pageSize: this.ifcPageSize,
      includeEnvironments: false
    });

    this.situViewApiClient.listPagedHomeIfcFiles(request)
      .then(response => {
        if (this.selectedCompany?.code !== company.code) {
          return;
        }
        this.ifcFiles = this.latestPerFile(response.items ?? []);
        this.ifcTotal = response.totalItems ?? 0;
        this.ifcLoading = false;
      })
      .catch(() => {
        this.ifcLoading = false;
        this.ifcFiles = [];
      });
  }

  /** The list has a row per upload; the picker wants a row per file, showing its newest upload. */
  private latestPerFile(items: IfcFile[]): IfcFile[] {
    const byFile = new Map<string, IfcFile>();
    for (const item of items) {
      const key = this.sourceKey(item);
      const seen = byFile.get(key);
      if (!seen || (item.version ?? 0) > (seen.version ?? 0)) {
        byFile.set(key, item);
      }
    }
    return Array.from(byFile.values());
  }

  onIfcPage(evt: PageEvent): void {
    this.ifcPageSize = evt.pageSize;
    this.ifcPageNumber = evt.pageIndex + 1;
    this.loadIfcFiles();
  }

  onIfcFilterChange(): void {
    clearTimeout(this.ifcFilterHandle);
    this.ifcFilterHandle = setTimeout(() => {
      this.ifcPageNumber = 1;
      this.loadIfcFiles();
    }, 350);
  }

  isSelected(file: IfcFile): boolean {
    return this.selected.has(this.sourceKey(file));
  }

  toggleFile(file: IfcFile, checked: boolean): void {
    this.toggleSource(new TrainingSource({
      home: file.home,
      name: file.name,
      isMaster: this.isMaster(file),
      version: 0
    }), checked);
  }

  isMaster(file: IfcFile): boolean {
    return (file.fileType ?? '').toLowerCase() === 'master';
  }

  // ---- selection --------------------------------------------------------------------------

  private sourceKey(s: { home?: string | undefined; name?: string | undefined }): string {
    return `${(s.home ?? '').toLowerCase()}|${(s.name ?? '').toLowerCase()}`;
  }

  toggleSource(source: TrainingSource, checked: boolean): void {
    const key = this.sourceKey(source);
    if (checked) {
      this.selected.set(key, source);
    } else {
      this.selected.delete(key);
    }
  }

  get selectedFiles(): TrainingSource[] {
    return Array.from(this.selected.values());
  }

  clearSelection(): void {
    this.selected.clear();
  }

  // ---- entry points -----------------------------------------------------------------------

  beginNew(): void {
    this.mode = 'new';
    this.basedOn = undefined;
    this.resetForm();
  }

  beginFix(): void {
    this.mode = 'fix';
    this.resetForm();
    this.baseOn(this.fixableRuns[0]);
  }

  /** Carry an earlier run's files into the form and open its home; the builder can still add or remove. */
  baseOn(run: TrainingRun | undefined): void {
    this.basedOn = run;
    this.selected.clear();
    const sources = run?.sources ?? [];
    for (const s of sources) {
      this.selected.set(this.sourceKey(s), new TrainingSource({
        home: s.home, name: s.name, isMaster: s.isMaster, version: 0
      }));
    }
    const homesInRun = Array.from(new Set(sources.map(s => s.home).filter((h): h is string => !!h)));
    if (homesInRun.length === 1) {
      this.chooseHome(homesInRun[0]);
    } else {
      this.selectedHome = undefined;
      this.homeQuery = '';
      this.homeFiles = [];
    }
  }

  cancelStart(): void {
    this.mode = 'idle';
    this.basedOn = undefined;
    this.resetForm();
  }

  private resetForm(): void {
    this.selected.clear();
    this.feedbackText = '';
    this.selectedHome = undefined;
    this.homeQuery = '';
    this.homeFiles = [];
    this.showAllUploads = false;
    this.ifcHomeFilter = '';
    this.ifcPageNumber = 1;
  }

  // ---- actions ----------------------------------------------------------------------------

  start(): void {
    const companyId = this.selectedCompany?.id;
    if (!companyId || !this.canStart) {
      return;
    }

    const sources = this.selectedFiles;

    this.starting = true;
    const request = new StartTrainingRunRequest({
      companyId,
      feedbackText: this.feedbackText?.trim() || undefined,
      sources
    });

    this.situViewApiClient.startTrainingRun(request)
      .then(response => {
        this.starting = false;
        if (response.success && response.run) {
          this.showMessage(`Run ${response.run.runNumber} started`);
          this.cancelStart();
          this.loadData(true);
        } else {
          this.showMessage(response.errorMessages?.[0] ?? 'Could not start the run');
          this.loadData(true);
        }
      })
      .catch(() => {
        this.starting = false;
        this.showMessage('Could not start the run');
      });
  }

  rerun(run: TrainingRun): void {
    const companyId = this.selectedCompany?.id;
    if (!companyId || run.runNumber === undefined || this.acting) {
      return;
    }

    this.acting = true;
    this.situViewApiClient.rerunTrainingRun(new RerunTrainingRunRequest({ companyId, runNumber: run.runNumber }))
      .then(response => {
        this.acting = false;
        if (response.success) {
          this.showMessage(`Run ${run.runNumber} started again`);
        } else {
          this.showMessage(response.errorMessages?.[0] ?? 'Could not start the run again');
        }
        this.loadData(true);
      })
      .catch(() => {
        this.acting = false;
        this.showMessage('Could not start the run again');
      });
  }

  abandon(run: TrainingRun): void {
    const companyId = this.selectedCompany?.id;
    if (!companyId || run.runNumber === undefined || this.acting) {
      return;
    }

    const data = new ConfirmDialogData();
    data.title = `Abandon run ${run.runNumber}`;
    data.message = isInProgress(run)
      ? 'This run has not finished. Abandoning it gives up on this attempt so a new run can start; ' +
        'its files and message are kept and it can be run again later.'
      : 'This run never reached a verdict. Abandoning it marks it as given up on; ' +
        'its files and message are kept and it can be run again later.';
    data.confirmText = 'Abandon';
    data.cancelText = 'Cancel';
    data.actionIcon = 'block';
    data.actionColour = 'warn';

    this.dialogService.open(data);
    this.dialogService.confirmed().subscribe(confirmed => {
      if (!confirmed) {
        return;
      }
      this.acting = true;
      const request = new AbandonTrainingRunRequest({
        companyId,
        runNumber: run.runNumber,
        reason: `Abandoned from the portal while ${describeStatus(run).toLowerCase()}`
      });
      this.situViewApiClient.abandonTrainingRun(request)
        .then(response => {
          this.acting = false;
          if (response.success) {
            this.showMessage(`Run ${run.runNumber} abandoned`);
            this.openRun(run, true);
          } else {
            this.showMessage(response.errorMessages?.[0] ?? 'Could not abandon this run');
          }
          this.loadData(true);
        })
        .catch(() => {
          this.acting = false;
          this.showMessage('Could not abandon this run');
        });
    });
  }

  promote(run: TrainingRun): void {
    const companyId = this.selectedCompany?.id;
    if (!companyId || run.runNumber === undefined || this.acting) {
      return;
    }

    const data = new ConfirmDialogData();
    data.title = `Promote run ${run.runNumber}`;
    data.message = this.hasErrorFlag(run)
      ? 'This run passed but raised an error flag, which is why it was not promoted on its own. ' +
        'Promoting makes its rules the ones every new conversion uses.'
      : 'Promoting makes this run\'s rules the ones every new conversion uses.';
    data.confirmText = 'Promote';
    data.cancelText = 'Cancel';
    data.actionIcon = 'publish';
    data.actionColour = 'primary';

    this.dialogService.open(data);
    this.dialogService.confirmed().subscribe(confirmed => {
      if (!confirmed) {
        return;
      }
      this.acting = true;
      const request = new PromoteTrainingRunRequest({ companyId, runNumber: run.runNumber, attempt: run.attempt });
      this.situViewApiClient.promoteTrainingRun(request)
        .then(response => {
          this.acting = false;
          if (response.success && response.rulesVersion) {
            this.showMessage(`Promoted to rules version ${response.rulesVersion.version}`);
            this.openRun(run, true);
          } else {
            this.showMessage(response.errorMessages?.[0] ?? 'Could not promote this run');
          }
          this.loadData(true);
        })
        .catch(() => {
          this.acting = false;
          this.showMessage('Could not promote this run');
        });
    });
  }

  private showMessage(message: string): void {
    this.snackBar.open(message, 'OK', { duration: 4000 });
  }
}
